<template>
  <BaseWidget
    title="Recent automation enrollments"
    :show-info-icon="true"
    info-tooltip="Latest automation enrollments within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      ref="widgetRef"
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <div class="enrollment-count">
        <div class="count-row">
          <span class="count">{{ filteredEnrollments.length }}</span>
          <ComparisonChip 
            :change="changePercentage" 
            :previous-value="formattedPreviousCount"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Recent automation enrollments"
          />
        </div>
        <div class="count-label-row">
          <span class="count-label">{{ dateRangeLabel }}</span>
          <a
            v-if="filteredEnrollments.length > 0 || hasMoreEnrollments"
            class="focused-view-all-link"
            @click.prevent="handleViewAllClick"
          >
            View all
          </a>
        </div>
      </div>
      
      <div
        v-if="!focused && (displayEnrollments.length > 0 || hasMoreEnrollments)"
        class="enrollments-list"
      >
        <div 
          v-for="(enrollment, index) in displayEnrollments" 
          :key="index" 
          class="enrollment-item"
          :class="{ 'has-status': enrollment.status, 'clickable': enrollment.workflowId && enrollment.id }"
          @click.stop.prevent="handleEnrollmentClickGuarded(enrollment)"
        >
          <div class="enrollment-row">
            <div class="enrollment-details">
              <div class="enrollment-workflow">
                {{ enrollment.workflowName }}
              </div>
              <div class="enrollment-meta">
                <span class="enrollment-time">{{ formatTimestamp(enrollment.createdTimestamp) }}</span>
                <span
                  v-if="enrollment.accountName"
                  class="enrollment-meta-sep"
                > • </span>
                <span
                  v-if="enrollment.accountName"
                  class="enrollment-account-name"
                >{{ enrollment.accountName }}</span>
              </div>
            </div>
            <div
              v-if="enrollment.status"
              class="enrollment-status-column"
            >
              <span
                class="enrollment-status-chip"
                :class="enrollmentStatusClass(enrollment.status)"
              >
                {{ formatEnrollmentStatus(enrollment.status) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!focused && displayEnrollments.length === 0 && filteredEnrollments.length === 0 && !hasMoreEnrollments"
        class="no-enrollments"
      >
        No enrollments in this time range
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useRecentAutomationEnrollmentsData } from '../composables/useWidgetData.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps, DATE_RANGE_LABELS } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'today')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Today'
})

// Calculate timestamps from date range
const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value)
})

const { data, loading, error, cleanup, refresh } = useRecentAutomationEnrollmentsData({
  priority: 1
})

// Fetch previous period data for comparison
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value)
})

// Filter enrollments for previous period
const previousFilteredEnrollments = computed(() => {
  if (!data.value?.workflowEnrollments?.data) return []
  const { from_timestamp, to_timestamp } = previousTimestamps.value
  return data.value.workflowEnrollments.data
    .map(enrollment => ({
      id: enrollment.workflow_enrollment_id,
      workflowId: enrollment.workflow?.workflow_id,
      workflowName: enrollment.workflow?.name || 'Unknown Workflow',
      accountName: enrollment.account?.name || 'Unknown Account',
      accountAvatar: getAvatarUrl(enrollment.account?.avatar?.url, enrollment.account?.name || enrollment.account?.account_id || 'Unknown'),
      createdTimestamp: enrollment.created_timestamp,
      status: enrollment.status || null
    }))
    .filter(enrollment => {
      const enrollmentTime = enrollment.createdTimestamp
      return enrollmentTime >= from_timestamp && enrollmentTime <= to_timestamp
    })
})

// Extract all enrollments from data
const allEnrollments = computed(() => {
  if (!data.value?.workflowEnrollments?.data) return []
  
  return data.value.workflowEnrollments.data.map(enrollment => ({
    id: enrollment.workflow_enrollment_id,
    workflowId: enrollment.workflow?.workflow_id,
    workflowName: enrollment.workflow?.name || 'Unknown Workflow',
    accountName: enrollment.account?.name || 'Unknown Account',
    accountAvatar: enrollment.account?.avatar?.url || null,
    createdTimestamp: enrollment.created_timestamp,
    status: enrollment.status || null
  }))
})

// Filter enrollments by date range
const filteredEnrollments = computed(() => {
  const { from_timestamp, to_timestamp } = timestamps.value
  return allEnrollments.value.filter(enrollment => {
    const enrollmentTime = enrollment.createdTimestamp
    return enrollmentTime >= from_timestamp && enrollmentTime <= to_timestamp
  })
})

// Calculate number of rows to display based on widget height (64px rows; leave room for "View more")
// Pattern: 2-row→2 slots, 3-row→4, 4-row→6, 5-row→8, 6-row→10... i.e. 2*(rowSpan-1)
const maxRowsToDisplay = computed(() => {
  if (props.rowSpan === 1) return 0
  return 2 * (props.rowSpan - 1)
})

// Check if there are more enrollments than we can display (only show when rowSpan > 1)
const hasMoreEnrollments = computed(() => {
  return props.rowSpan > 1 && filteredEnrollments.value.length > maxRowsToDisplay.value
})

// Calculate remaining count
const remainingEnrollmentsCount = computed(() => {
  if (!hasMoreEnrollments.value) return 0
  return filteredEnrollments.value.length - displayEnrollments.value.length
})

// Display enrollments based on widget height (leave room for "more" row if needed)
const displayEnrollments = computed(() => {
  if (maxRowsToDisplay.value === 0) {
    return []
  }
  return filteredEnrollments.value.slice(0, maxRowsToDisplay.value)
})

// Calculate percentage change
const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value) {
    return null
  }
  
  const current = filteredEnrollments.value.length
  const previous = previousFilteredEnrollments.value.length
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change
  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10 // Round to 1 decimal place
})

// Format previous count for display
const formattedPreviousCount = computed(() => {
  if (!data.value) return null
  const count = previousFilteredEnrollments.value.length
  if (count === 0) return '0'
  return count.toString()
})

// Format enrollment status for display (e.g. "active" -> "In progress")
const formatEnrollmentStatus = (status) => {
  if (!status) return ''
  const s = String(status).toLowerCase()
  if (s === 'active' || s === 'in_progress' || s === 'in progress') return 'In progress'
  if (s === 'completed' || s === 'complete') return 'Completed'
  if (s === 'paused' || s === 'pause') return 'Paused'
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// CSS class for status chip styling (green = completed, blue = in progress)
const enrollmentStatusClass = (status) => {
  if (!status) return 'enrollment-status-neutral'
  const s = String(status).toLowerCase()
  if (s === 'active' || s === 'in_progress' || s === 'in progress') return 'enrollment-status-in-progress'
  if (s === 'completed' || s === 'complete') return 'enrollment-status-completed'
  if (s === 'paused' || s === 'pause') return 'enrollment-status-paused'
  if (s === 'cancelled' || s === 'canceled') return 'enrollment-status-cancelled'
  return 'enrollment-status-neutral'
}

// Format timestamp for display
const formatTimestamp = (timestamp) => {
  if (!timestamp) return ''
  
  // Convert timestamp (assuming Unix seconds) to Date
  const date = new Date(timestamp * 1000)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) {
    return 'Just now'
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined })
  }
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''
  
  // First try current URL
  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  
  // Try referrer if not found
  if (!clientId || !canvasUUID) {
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) {
          clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      } catch (e) {
      }
    }
  }
  
  // Try parent window URL if accessible (may fail due to CORS)
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = window.parent.location.href
        const parentUrlObj = new URL(parentUrl)
        const parentParams = new URLSearchParams(parentUrlObj.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      }
    } catch (e) {
      // CORS - can't access parent location
    }
  }
  
  return {
    client_id: clientId,
    canvasUUID: canvasUUID
  }
}

// Handle "View all" / "View more" click - navigate to automations page (same as Most impactful automations)
const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const navigationUrl = "/automations"

  const message = {
    command: 'ReplaceUrl',
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('❌ Error posting ReplaceUrl message:', e)
  }
}

// Track if we're currently processing a click to prevent double-firing
let isProcessingClick = false

// Handle enrollment click - open enrollment panel in Optix
const handleEnrollmentClickGuarded = guard((enrollment) => {
  // Prevent double-firing (very short timeout since we fixed duplicate handlers)
  if (isProcessingClick) {
    return
  }
  
  isProcessingClick = true
  
  // Reset the flag after a short delay
  setTimeout(() => {
    isProcessingClick = false
  }, 200)
  
  
  if (!enrollment) {
    console.error('❌ Enrollment is null or undefined')
    isProcessingClick = false
    return
  }
  
  if (!enrollment.workflowId || !enrollment.id) {
    console.warn('⚠️ Enrollment missing workflowId or id:', {
      id: enrollment.id,
      workflowId: enrollment.workflowId,
      fullEnrollment: enrollment
    })
    console.warn('⚠️ Cannot open enrollment panel without workflowId and enrollmentId')
    isProcessingClick = false
    return
  }
  
  const params = getUrlParams()
  
  // Generate a canvasUUID if not found (some widgets use fallback)
  const finalCanvasUUID = params.canvasUUID || `canvas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  
  // Send WorkflowEnrollment message to parent window
  // Based on console logs from native Optix, the message format is:
  // WorkflowEnrollment { workflowId, enrollmentId, optix-placement, canvasUUID, client_id }
  const messageData = {
    workflowId: enrollment.workflowId.toString(),
    enrollmentId: enrollment.id.toString(),
    'optix-placement': 'custom',
    canvasUUID: finalCanvasUUID,
    client_id: params.client_id || undefined
  }
  
  // Try both formats - direct object and command structure
  const directMessage = messageData
  const commandMessage = {
    command: "WorkflowEnrollment",
    payload: messageData
  }
  
  
  try {
    if (window.parent && window.parent !== window) {
      // Send direct format first
      window.parent.postMessage(directMessage, "*")
      
      // Also send command format (one of these should work)
      setTimeout(() => {
        window.parent.postMessage(commandMessage, "*")
      }, 50)
      
      // Also try window.top in case of nested iframes
      if (window.top && window.top !== window && window.top !== window.parent) {
        window.top.postMessage(directMessage, "*")
        setTimeout(() => {
          window.top.postMessage(commandMessage, "*")
        }, 50)
      }
    } else {
      console.warn('⚠️ Not in an iframe, cannot post message to parent')
      isProcessingClick = false
    }
  } catch (error) {
    console.error('❌ Error posting message:', error)
    isProcessingClick = false
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.enrollment-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding-bottom: 10px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.enrollments-list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  min-width: 0;
  max-height: none;
  width: 100%;
}

.enrollment-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
}

.enrollment-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.enrollment-item:last-child {
  border-bottom: none;
}

.enrollment-item:not(.enrollment-item-more):hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.enrollment-item-more {
  min-height: 32px;
  padding: 4px 4px;
}

.enrollment-item-more .enrollment-row {
  width: 100%;
}

.enrollment-item-more .enrollment-row-more {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.enrollment-item-more .view-more-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.enrollment-item-more .view-more-link:hover {
  color: #2563eb;
}

.enrollment-item-more .enrollment-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.enrollment-item-more .enrollment-workflow {
  margin-bottom: 0;
}

.enrollment-item.clickable {
  cursor: pointer;
}

.enrollment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  flex: 1;
  height: 100%;
  min-height: 0;
  width: 100%;
}

.enrollment-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  flex: 1;
  min-height: 0;
}

.enrollment-workflow {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.enrollment-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.enrollment-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 8px;
}

.enrollment-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.enrollment-status-column {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: auto;
}

.enrollment-status-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
}

.enrollment-status-completed {
  background-color: #DBEDDB;
  color: #1C3829;
}

.enrollment-status-in-progress {
  background-color: #DBEAFE;
  color: #1e40af;
}

.enrollment-status-paused {
  background-color: #FDECC8;
  color: #402C1B;
}

.enrollment-status-cancelled {
  background-color: #f3f4f6;
  color: #6b7280;
}

.enrollment-status-neutral {
  background-color: #f3f4f6;
  color: #6b7280;
}

.enrollment-time {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.enrollment-view-all {
  cursor: pointer;
  transition: color 0.2s ease;
}

.enrollment-view-all:hover {
  color: #2563eb;
}

.no-enrollments {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
